import React, { useState, useEffect } from "react";
import {
  Container, Typography, Box, Stepper, Step, StepLabel, Button, Card, CardContent, Grid,
  FormControl, InputLabel, Select, MenuItem, Avatar, Chip, Paper, Alert, Snackbar,
  Dialog, DialogTitle, DialogContent, DialogActions, Autocomplete, TextField, ToggleButton, ToggleButtonGroup
} from "@mui/material";
import { styled } from "@mui/material/styles";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import CategoryIcon from "@mui/icons-material/Category";
import SaveIcon from "@mui/icons-material/Save";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import LocationCityIcon from "@mui/icons-material/LocationCity";
import BusinessIcon from "@mui/icons-material/Business";
import GroupsIcon from "@mui/icons-material/Groups";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import WarningIcon from "@mui/icons-material/Warning";
import { apiService, handleApiError } from '../config/api';

const StyledContainer = styled(Container)({
  padding: "2rem",
  maxWidth: "1200px !important",
});

const StyledCard = styled(Card)({
  borderRadius: "16px",
  boxShadow: "0 4px 20px rgba(67, 41, 24, 0.08)",
  border: "1px solid #E8E3DF",
});

const StyledButton = styled(Button)({
  backgroundColor: "#432918",
  color: "white",
  fontWeight: 600,
  padding: "12px 24px",
  borderRadius: "8px",
  "&:hover": { backgroundColor: "#2A1A0F" },
});

const DeleteButton = styled(Button)({
  backgroundColor: "#d32f2f",
  color: "white",
  fontWeight: 600,
  padding: "12px 24px",
  borderRadius: "8px",
  "&:hover": { backgroundColor: "#b71c1c" },
});

const CATEGORIA_CONFIGS = {
  1: { nombre: "FCN", icono: <AccountBalanceIcon />, color: "#1976D2", descripcion: "Fondo Cafetero Nacional" },
  2: { nombre: "DEPARTAMENTAL", icono: <LocationCityIcon />, color: "#2E7D32", descripcion: "Representante departamental" },
  3: { nombre: "MUNICIPAL", icono: <BusinessIcon />, color: "#FF8F00", descripcion: "Representante municipal" },
  4: { nombre: "JUNTA DIRECTIVA", icono: <GroupsIcon />, color: "#9C27B0", descripcion: "Miembro de junta directiva" },
  5: { nombre: "GUBERNAMENTAL", icono: <AccountBalanceIcon />, color: "#D32F2F", descripcion: "Representante gubernamental" }
};

const getCategoriasActivas = (representante) =>
  (representante?.categorias || []).filter(cat => cat.Estado !== 'Inactivo');

const RegistroCategorias = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [categoriaSeleccionada, setCategoriaSeleccionada] = useState(null);  
  const [representanteSeleccionado, setRepresentanteSeleccionado] = useState(null);
  const [formularioData, setFormularioData] = useState({});
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [confirmDialog, setConfirmDialog] = useState(false);
  const [mode, setMode] = useState('add'); // 'add', 'edit', 'delete'
  
  // Estados para datos
  const [representantes, setRepresentantes] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [puestos, setPuestos] = useState([]);

  // Steps dinámicos según el modo
  const getSteps = () => {
    switch (mode) {
      case 'add':
        return ['Seleccionar Representante', 'Seleccionar Categoría', 'Completar Cargo', 'Confirmar'];
      case 'edit':
        return ['Seleccionar Representante', 'Seleccionar Categoría', 'Editar Cargo', 'Confirmar'];
      case 'delete':
        return ['Seleccionar Representante', 'Seleccionar Categoría a Inactivar', 'Confirmar Inactivación'];
      default:
        return [];
    }
  };

  const steps = getSteps();

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const datos = await apiService.utils.getDatosCategorias();
        
        setRepresentantes(datos.representantes || []);
        setCategorias(datos.categorias || []);
        setPuestos(datos.puestos || []);
        
      } catch (error) {
        const errorMessage = handleApiError(error, "Error cargando datos iniciales");
        setSnackbar({ 
          open: true, 
          message: errorMessage, 
          severity: "error" 
        });
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);

  const handleModeChange = (event, newMode) => {
    if (newMode !== null) {
      setMode(newMode);
      setActiveStep(0);
      setCategoriaSeleccionada(null);
      setFormularioData({});
    }
  };
  const handleNext = () => {
    if (activeStep === 0 && !representanteSeleccionado) {
      setSnackbar({ open: true, message: "Selecciona un representante", severity: "warning" });
      return;
    }
    if (activeStep === 1 && !categoriaSeleccionada) {
      setSnackbar({ open: true, message: "Selecciona una categoría", severity: "warning" });
      return;
    }
    setActiveStep(prev => prev + 1);
  };

  const handleBack = () => {
    setActiveStep(prev => prev - 1);
  };

  const handleCategoriaSelect = (categoriaId) => {
    const parsedId = parseInt(categoriaId);
    if (isNaN(parsedId)) {
      setSnackbar({ open: true, message: "Categoría inválida seleccionada", severity: "error" });
      return;
    }
    setCategoriaSeleccionada(parsedId);
    
    if (mode === 'edit') {
      const currentCat = getCategoriasActivas(representanteSeleccionado).find(cat => (cat.CategoriaFK || cat.Id) === parsedId);
      setFormularioData({ cargoFK: currentCat?.CargoFK ? parseInt(currentCat.CargoFK) : '' });
    } else {
      setFormularioData({});
    }
  };

  const handleRepresentanteSelect = (representante) => {
    setRepresentanteSeleccionado(representante);
    setCategoriaSeleccionada(null);
    setFormularioData({});
  };

  const handleInputChange = (field, value) => {
    setFormularioData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const codRepresentante = parseInt(representanteSeleccionado?.cod_representante);
      if (!codRepresentante || isNaN(codRepresentante)) {
        throw new Error("Código de representante inválido");
      }

      const categoriaId = parseInt(categoriaSeleccionada);
      if (!categoriaId || isNaN(categoriaId)) {
        throw new Error("Categoría seleccionada inválida");
      }
      const categoriasActuales = getCategoriasActivas(representanteSeleccionado);
     
      
      let payload;
      let successMessage;

      switch (mode) {
        case 'add':
          const yaAsignada = categoriasActuales.some(cat => (cat.CategoriaFK || cat.Id) === categoriaId);
          if (yaAsignada) {
            setSnackbar({ open: true, message: "La categoría ya está asignada", severity: "warning" });
            setLoading(false);
            return;
          }

          // ENVIAR TODAS LAS CATEGORÍAS: existentes + nueva
          const nuevaCategoria = {
            CategoriaFK: categoriaId,
            CargoFK: formularioData.cargoFK ? parseInt(formularioData.cargoFK) : null
          };

          // Función helper para preservar datos de categorías existentes
          const preservarCategoriaExistente = (cat) => ({
            CategoriaFK: parseInt(cat.CategoriaFK || cat.Id),
            CargoFK: cat.CargoFK ? parseInt(cat.CargoFK) : null
          });

          const todasLasCategorias = [
            // Primero las existentes (para que se mantengan)
            ...categoriasActuales.map(cat => preservarCategoriaExistente(cat)),
            // Luego la nueva
            nuevaCategoria
          ];

          payload = {
            categorias: todasLasCategorias
          };
          successMessage = `Categoría ${CATEGORIA_CONFIGS[categoriaId].nombre} asignada exitosamente`;
        
          break;

        case 'edit':
          const categoriaExisteEdit = categoriasActuales.some(cat => (cat.CategoriaFK || cat.Id) === categoriaId);
          if (!categoriaExisteEdit) {
            setSnackbar({ open: true, message: "La categoría no está asignada al representante", severity: "warning" });
            setLoading(false);
            return;
          }

          // ENVIAR SOLO LA CATEGORÍA A EDITAR
          payload = {
            categorias: [{
              CategoriaFK: categoriaId,
              CargoFK: formularioData.cargoFK ? parseInt(formularioData.cargoFK) : null
            }]
          };

          successMessage = `Cargo actualizado para ${CATEGORIA_CONFIGS[categoriaId].nombre}`;
          break;

        case 'delete':
          const categoriaExisteDelete = categoriasActuales.some(cat => (cat.CategoriaFK || cat.Id) === categoriaId);
          if (!categoriaExisteDelete) {
            setSnackbar({ open: true, message: "La categoría no está asignada al representante", severity: "warning" });
            setLoading(false);
            return;
          }

          // INACTIVAR LA CATEGORÍA (se conserva en el histórico, no se borra)
          payload = {
            categorias: [{
              CategoriaFK: categoriaId,
              Estado: "Inactivo"
            }]
          };

          successMessage = `Categoría ${CATEGORIA_CONFIGS[categoriaId].nombre} inactivada exitosamente`;
        
          break;

        default:
          throw new Error("Modo de operación no válido");
      }
      const result = await apiService.representantes.update(codRepresentante, payload);
      
      setSnackbar({ open: true, message: successMessage, severity: "success" });
      setConfirmDialog(false);
      setActiveStep(0);
      setCategoriaSeleccionada(null);
      setFormularioData({});
      
      // Refrescar datos usando el API service
      await refreshData();

    } catch (error) {
      const errorMessage = handleApiError(error, "Error al procesar la operación");
      setSnackbar({ open: true, message: errorMessage, severity: "error" });
    } finally {
      setLoading(false);
    }
  };
  const refreshData = async () => {
    try {
      const representantesActualizados = await apiService.representantes.getAll();
      setRepresentantes(representantesActualizados);
      if (representanteSeleccionado) {
        const updatedRep = representantesActualizados.find(
          rep => rep.cod_representante === representanteSeleccionado.cod_representante
        );
        if (updatedRep) {
          setRepresentanteSeleccionado(updatedRep);
        }
      }
    } catch (error) {
    }
  };

  const renderCampoFormulario = (campo) => {
    const config = CATEGORIA_CONFIGS[categoriaSeleccionada];
    if (!config) return null;

    switch (campo) {
      case 'cargo':
        return (
          <FormControl key={campo} fullWidth>
            <InputLabel>Cargo en {config.nombre} (opcional)</InputLabel>
            <Select
              value={formularioData.cargoFK || ''}
              label={`Cargo en ${config.nombre} (opcional)`}
              onChange={(e) => handleInputChange('cargoFK', e.target.value)}
            >
              <MenuItem value=""><em>Sin cargo</em></MenuItem>
              {puestos.map(puesto => (
                <MenuItem key={puesto.IdPuesto} value={puesto.IdPuesto}>
                  {puesto.Descripcion}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );
      default:
        return null;
    }
  };

  const StepContent = ({ step }) => {
    switch (step) {
      case 0:
        return (
          <Box>
            <Typography variant="h6" sx={{ mb: 3, textAlign: 'center', color: '#432918' }}>
              {representanteSeleccionado ? 'Representante Seleccionado' : 'Seleccionar Representante'}
            </Typography>

<Autocomplete
  options={representantes}
  getOptionLabel={(option) => `${option.nombre_representante || ''}`}
  value={representanteSeleccionado}
  onChange={(event, newValue) => handleRepresentanteSelect(newValue)}
  filterOptions={(options, params) => {
    const { inputValue } = params;
    
    // Si no hay entrada, no mostrar opciones
    if (!inputValue || inputValue.trim() === '') {
      return [];
    }
    
    // Normalizar la entrada
    const filterValue = inputValue
      .toLowerCase()
      .trim()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
    
    // Separar en palabras
    const searchWords = filterValue.split(/\s+/).filter(word => word.length > 0);
    
    return options
      .filter(option => {
        const nombre = (option.nombre_representante || '')
          .toLowerCase()
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '');
        
        // Requerir que todas las palabras de búsqueda coincidan al inicio de alguna palabra del nombre
        return searchWords.every(searchWord => 
          nombre.split(/\s+/).some(nombreWord => 
            nombreWord.startsWith(searchWord)
          )
        );
      })
      .slice(0, 10); // Limitar a 10 resultados
  }}
  isOptionEqualToValue={(option, value) => 
    option?.cod_representante === value?.cod_representante
  }
  noOptionsText="No se encontraron representantes"
  renderInput={(params) => (
    <TextField 
      {...params} 
      label="Buscar Representante" 
      placeholder="Escribe el nombre del representante" 
      fullWidth
      onChange={(e) => {
        if (e.target.value.length >= 3 || e.target.value.length === 0) {
          params.inputProps.onChange(e);
        }
      }}
    />
  )}
  renderOption={(props, option) => (
    <Box component="li" {...props}>
      <Avatar src={option.fotografia || "/placeholder-avatar.png"} sx={{ width: 40, height: 40, mr: 2 }} />
      <Box>
        <Typography variant="body1" sx={{ fontWeight: 600 }}>
          {option.nombre_representante}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {option.dni} • {getCategoriasActivas(option).length} categoría(s)
        </Typography>
      </Box>
    </Box>
  )}
/>


            {representanteSeleccionado && (
              <Paper sx={{ p: 3, mt: 3, backgroundColor: '#F7F5F3' }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item>
                    <Avatar src={representanteSeleccionado.fotografia} sx={{ width: 60, height: 60 }} />
                  </Grid>
                  <Grid item xs>
                    <Typography variant="h6">{representanteSeleccionado.nombre_representante}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      DNI: {representanteSeleccionado.dni}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Categorías: {getCategoriasActivas(representanteSeleccionado).length}
                    </Typography>
                    {getCategoriasActivas(representanteSeleccionado).length > 0 && (
                      <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {getCategoriasActivas(representanteSeleccionado).map((cat, index) => (
                          <Chip
                            key={index}
                            label={`${CATEGORIA_CONFIGS[cat.CategoriaFK || cat.Id]?.nombre || cat.Descripcion}${cat.CargoFK ? ` - ${puestos.find(p => p.IdPuesto === cat.CargoFK)?.Descripcion || cat.Cargo || 'Cargo'}` : (cat.Cargo ? ` - ${cat.Cargo}` : '')}`}
                            size="small"
                            variant="outlined"
                            sx={{ 
                              borderColor: CATEGORIA_CONFIGS[cat.CategoriaFK || cat.Id]?.color || '#432918', 
                              color: CATEGORIA_CONFIGS[cat.CategoriaFK || cat.Id]?.color || '#432918' 
                            }}
                          />
                        ))}
                      </Box>
                    )}
                  </Grid>
                </Grid>
              </Paper>
            )}
          </Box>
        );

      case 1:
        const titleMap = {
          'add': 'Seleccionar Nueva Categoría',
          'edit': 'Seleccionar Categoría a Editar',
          'delete': 'Seleccionar Categoría a Inactivar'
        };

        return (
          <Box>
            <Typography variant="h6" sx={{ mb: 3, textAlign: 'center', color: '#432918' }}>
              {titleMap[mode]}
            </Typography>
            
            {representanteSeleccionado && (
              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="body2">
               {representanteSeleccionado.nombre_representante} tiene{' '}
               {getCategoriasActivas(representanteSeleccionado).length} categoría(s) asignada(s).
                </Typography>
              </Alert>
            )}

            <Grid container spacing={3}>
              {(mode === 'edit' || mode === 'delete' 
                ? getCategoriasActivas(representanteSeleccionado)
                : Object.entries(CATEGORIA_CONFIGS)
              ).map((item) => {
                let id, config, currentCargo = null;
                
                if (mode === 'edit' || mode === 'delete') {
                  const cat = item;
                  id = cat.CategoriaFK || cat.Id;
                  config = CATEGORIA_CONFIGS[id];
                  currentCargo = cat.CargoFK 
                    ? puestos.find(p => p.IdPuesto === cat.CargoFK)?.Descripcion 
                    : cat.Cargo;
                  if (!config) return null;
                } else {
                  [id, config] = item;
                }

                const isDisabled = (mode === 'add' && getCategoriasActivas(representanteSeleccionado).some(cat => (cat.CategoriaFK || cat.Id) === parseInt(id)));

                return (
                  <Grid item xs={12} sm={6} md={4} key={id}>
                    <Card
                      sx={{
                        cursor: isDisabled ? 'not-allowed' : 'pointer',
                        border: categoriaSeleccionada === parseInt(id) ? `2px solid ${config.color}` : '1px solid #E8E3DF',
                        opacity: isDisabled ? 0.6 : 1,
                        '&:hover': isDisabled ? {} : { transform: 'translateY(-2px)', boxShadow: 3 },
                        transition: 'all 0.3s',
                        backgroundColor: mode === 'delete' ? '#ffebee' : 'white'
                      }}
                      onClick={() => !isDisabled && handleCategoriaSelect(parseInt(id))}
                    >
                      <CardContent sx={{ textAlign: 'center', p: 3 }}>
                        <Box sx={{ color: config.color, mb: 2 }}>
                          {React.cloneElement(config.icono, { sx: { fontSize: 48 } })}
                        </Box>
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                          {config.nombre}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          {config.descripcion}
                        </Typography>
                        
                        {currentCargo && (
                          <Typography variant="caption" sx={{ display: 'block', mb: 1, fontWeight: 600 }}>
                            Cargo actual: {currentCargo}
                          </Typography>
                        )}

                        {mode === 'add' && isDisabled ? (
                          <Chip label="Ya Asignada" color="default" sx={{ backgroundColor: '#e0e0e0' }} />
                        ) : categoriaSeleccionada === parseInt(id) ? (
                          <Chip 
                            label="Seleccionada" 
                            color="primary" 
                            sx={{ backgroundColor: config.color }} 
                          />
                        ) : (
                          <Chip
                            label={mode === 'delete' ? 'Inactivar' : 'Disponible'}
                            variant="outlined" 
                            sx={{ 
                              borderColor: config.color, 
                              color: config.color,
                              ...(mode === 'delete' && { borderColor: '#f44336', color: '#f44336' })
                            }} 
                          />
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
              
              {(mode === 'edit' || mode === 'delete') && getCategoriasActivas(representanteSeleccionado).length === 0 && (
                <Grid item xs={12}>
                  <Typography sx={{ textAlign: 'center', color: '#666', py: 4 }}>
                    Este representante no tiene categorías asignadas para {mode === 'edit' ? 'editar' : 'inactivar'}.
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Box>
        );

      case 2:
        if (mode === 'delete') {
          return (
            <Box sx={{ textAlign: 'center' }}>
              <WarningIcon sx={{ fontSize: 64, color: '#f44336', mb: 2 }} />
              <Typography variant="h5" sx={{ mb: 2, color: '#f44336' }}>
                 Confirmar Inactivación de Categoría
              </Typography>
              <Paper sx={{ p: 3, backgroundColor: '#ffebee', border: '2px solid #f44336' }}>
                <Typography variant="h6" sx={{ mb: 2, color: '#d32f2f' }}>
                  ATENCIÓN: Esta acción no se puede deshacer
                </Typography>
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={12} sm={4}>
                    <Avatar src={representanteSeleccionado?.fotografia} sx={{ width: 80, height: 80, mx: 'auto', mb: 2 }} />
                  </Grid>
                  <Grid item xs={12} sm={8} sx={{ textAlign: 'left' }}>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                      Representante: {representanteSeleccionado?.nombre_representante}
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                      DNI: {representanteSeleccionado?.dni}
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1, color: '#d32f2f' }}>
                     Categoría a Inactivar: {CATEGORIA_CONFIGS[categoriaSeleccionada]?.nombre}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#666', mt: 2 }}>
                      Categorías activas restantes: {getCategoriasActivas(representanteSeleccionado).length - 1}
                    </Typography>
                  </Grid>
                </Grid>
                <Alert severity="error" sx={{ mt: 2 }}>
                  Se inactivará la categoría {CATEGORIA_CONFIGS[categoriaSeleccionada]?.nombre} del representante. Quedará guardada en el histórico pero dejará de contar como asignación activa.
                  {getCategoriasActivas(representanteSeleccionado).length === 1 && (
                    <><br /><strong>Nota:</strong> El representante quedará sin categorías activas asignadas.</>
                  )}
                </Alert>
              </Paper>
            </Box>
          );
        } else {
          const config = CATEGORIA_CONFIGS[categoriaSeleccionada];
          const camposToShow = ['cargo'];
          return (
            <Box>
              <Typography variant="h6" sx={{ mb: 3, textAlign: 'center', color: '#432918' }}>
                {mode === 'edit' ? `Actualizar Cargo para ${config?.nombre}` : `Completar Cargo para ${config?.nombre}`}
              </Typography>
              <Typography variant="body2" sx={{ mb: 2, textAlign: 'center', color: '#666' }}>
                Seleccione un cargo si aplica (opcional).
              </Typography>
              <Grid container spacing={3}>
                {camposToShow.map(campo => (
                  <Grid item xs={12} sm={6} key={campo}>
                    {renderCampoFormulario(campo)}
                  </Grid>
                ))}
              </Grid>
            </Box>
          );
        }

      case 3:
        if (mode !== 'delete') {
          return (
            <Box sx={{ textAlign: 'center' }}>
              <CheckCircleIcon sx={{ fontSize: 64, color: '#4CAF50', mb: 2 }} />
              <Typography variant="h5" sx={{ mb: 2, color: '#432918' }}>
                Confirmar {mode === 'edit' ? 'Actualización de Cargo' : 'Asignación de Categoría'}
              </Typography>
              <Paper sx={{ p: 3, backgroundColor: '#F7F5F3' }}>
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={12} sm={4}>
                    <Avatar src={representanteSeleccionado?.fotografia} sx={{ width: 80, height: 80, mx: 'auto', mb: 2 }} />
                  </Grid>
                  <Grid item xs={12} sm={8} sx={{ textAlign: 'left' }}>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                      Representante: {representanteSeleccionado?.nombre_representante}
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                      DNI: {representanteSeleccionado?.dni}
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                      Categoría: {CATEGORIA_CONFIGS[categoriaSeleccionada]?.nombre}
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                      Cargo: {formularioData.cargoFK ? puestos.find(p => p.IdPuesto === parseInt(formularioData.cargoFK))?.Descripcion : 'Sin cargo'}
                    </Typography>
                    <Chip
                      label={mode === 'edit' ? 'Actualizar cargo' : '+ Nueva categoría'}
                      color="primary"
                      sx={{ mt: 1, backgroundColor: CATEGORIA_CONFIGS[categoriaSeleccionada]?.color }}
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Box>
          );
        }
        return null;

      default:
        return null;
    }
  };

  return (
    <>
      <StyledContainer>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <PersonAddIcon sx={{ fontSize: 48, color: '#432918', mb: 2 }} />
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#432918', mb: 1 }}>
            Gestión de Categorías
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Administra representantes por categorias y cargos 
          </Typography>
        </Box>

        <StyledCard sx={{ p: 4 }}>
          <Box sx={{ mb: 4, textAlign: 'center' }}>
            <Typography variant="h6" sx={{ mb: 2, color: '#432918' }}>
              Selecciona una operación
            </Typography>
            <ToggleButtonGroup
              value={mode}
              exclusive
              onChange={handleModeChange}
              aria-label="modo de operación"
              sx={{ 
                '& .MuiToggleButton-root': {
                  borderRadius: '8px',
                  margin: '0 4px',
                  padding: '12px 20px',
                  fontWeight: 600,
                  transition: 'all 0.3s ease',
                  '&.Mui-selected': {
                    backgroundColor: '#432918',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: '#2A1A0F',
                    },
                  },
                  '&:hover': {
                    backgroundColor: '#f5f5f5',
                  },
                },
              }}
            >
              <ToggleButton value="add" aria-label="agregar">
                <AddIcon sx={{ mr: 1 }} />
                Agregar Categoría
              </ToggleButton>
              <ToggleButton value="edit" aria-label="editar">
                <EditIcon sx={{ mr: 1 }} />
                Editar Cargo
              </ToggleButton>
              <ToggleButton value="delete" aria-label="inactivar">
                <DeleteIcon sx={{ mr: 1 }} />
                Inactivar Categoría
              </ToggleButton>
            </ToggleButtonGroup>
            
            {representanteSeleccionado && (
              <Alert 
                severity="info" 
                sx={{ 
                  mt: 3, 
                  maxWidth: '600px', 
                  mx: 'auto',
                  '& .MuiAlert-message': {
                    textAlign: 'left'
                  }
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Representante seleccionado: {representanteSeleccionado.nombre_representante}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {mode === 'add' && 'Agregar nueva categoría a este representante'}
                  {mode === 'edit' && 'Editar cargo de una categoría existente'}
                  {mode === 'delete' && 'Inactivar una categoría de este representante'}
                </Typography>
              </Alert>
            )}
          </Box>

          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {steps.map(label => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          <StepContent step={activeStep} />

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
            <Button disabled={activeStep === 0} onClick={handleBack} variant="outlined">
              Atrás
            </Button>
            
            {activeStep === steps.length - 1 ? (
              mode === 'delete' ? (
                <DeleteButton onClick={() => setConfirmDialog(true)} startIcon={<DeleteIcon />} disabled={loading}>
                  {loading ? 'Inactivando...' : 'Inactivar Categoría'}
                </DeleteButton>
              ) : (
                <StyledButton onClick={() => setConfirmDialog(true)} startIcon={<SaveIcon />} disabled={loading}>
                  {loading ? 'Guardando...' : 'Guardar'}
                </StyledButton>
              )
            ) : (
              <StyledButton onClick={handleNext}>
                Siguiente
              </StyledButton>
            )}
          </Box>
        </StyledCard>

        <Dialog open={confirmDialog} onClose={() => setConfirmDialog(false)}>
          <DialogTitle 
            sx={{ 
              backgroundColor: mode === 'delete' ? '#ffebee' : 'inherit', 
              color: mode === 'delete' ? '#d32f2f' : 'inherit' 
            }}
          >
            {mode === 'delete' && <WarningIcon sx={{ mr: 1, verticalAlign: 'middle' }} />}
            {mode === 'add' && 'Confirmar Asignación'}
            {mode === 'edit' && 'Confirmar Actualización de Cargo'}
            {mode === 'delete' && 'Confirmar Inactivación de Categoría'}
          </DialogTitle>
          <DialogContent>
            <Typography>
              ¿Estás seguro de {
                mode === 'add' ? 'asignar la categoría' :
                mode === 'edit' ? 'actualizar el cargo para la categoría' :
                'inactivar la categoría'
              } {CATEGORIA_CONFIGS[categoriaSeleccionada]?.nombre}
              {mode !== 'delete' && formularioData.cargoFK ?
                ` con el cargo ${puestos.find(p => p.IdPuesto === parseInt(formularioData.cargoFK))?.Descripcion}` :
                mode !== 'delete' ? ' sin cargo' : ''
              } {mode === 'add' ? 'para' : mode === 'edit' ? 'de' : 'de'}{' '}
              {representanteSeleccionado?.nombre_representante}?
            </Typography>

            {mode === 'delete' && (
              <Alert severity="error" sx={{ mt: 2 }}>
                 Esta acción no se puede deshacer, pero la categoría quedará guardada en el histórico.
                <br />El representante quedará con {getCategoriasActivas(representanteSeleccionado).length - 1} categoría(s) activa(s).
                {getCategoriasActivas(representanteSeleccionado).length === 1 && (
                  <><br /><strong>Nota:</strong> El representante quedará sin categorías activas asignadas.</>
                )}
              </Alert>
            )}
            
            <Box sx={{ mt: 2, p: 2, backgroundColor: '#F7F5F3', borderRadius: 1 }}>
              
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmDialog(false)}>Cancelar</Button>
            {mode === 'delete' ? (
              <DeleteButton onClick={handleSubmit} disabled={loading} startIcon={<DeleteIcon />}>
                {loading ? 'Inactivando...' : 'Confirmar Inactivación'}
              </DeleteButton>
            ) : (
              <StyledButton onClick={handleSubmit} disabled={loading}>
                {loading ? 'Guardando...' : 'Confirmar'}
              </StyledButton>
            )}
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
        </Snackbar>
      </StyledContainer>
    </>
  );
};

export default RegistroCategorias;